import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { uiApi } from "@/api/ui.api";
import { useAppSelector } from "@/hooks/useAppSelector";
import { searchApi, type GlobalSearchResult } from "@/api/search.api";
import { useDebounce } from "@/hooks/useDebounce";
import { queryKeys, tierOptions } from "@/lib/query";
import { cn } from "@/utils/cn";
import { resolveBackendLabel, resolveGroupLabel } from "@/i18n/navLabel";

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  to: string;
  kind: "nav" | "entity";
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  // Navigation labels resolve from the navigation namespace.
  const { t } = useTranslation(["common", "navigation"]);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 280);
  const [entityResults, setEntityResults] = useState<GlobalSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const sessionNav = useAppSelector((s) => s.session.navigation);
  const sessionLoaded = useAppSelector((s) => s.session.loaded);

  const { data: fetchedNav } = useQuery({
    queryKey: queryKeys.ui.menus(undefined),
    queryFn: () => uiApi.menus(undefined),
    enabled: sessionLoaded && !sessionNav?.length,
    ...tierOptions('master'),
  });

  const navigation = sessionNav?.length ? sessionNav : (fetchedNav ?? []);

  const navItems = useMemo(() => {
    const commands: CommandItem[] = [];
    for (const group of navigation) {
      const groupLabel = resolveGroupLabel(group.id, group.label ?? group.id, t);
      for (const item of group.items ?? []) {
        if (!item.route) continue;
        commands.push({
          id: `nav-${group.id}-${item.id ?? item.route}`,
          label: resolveBackendLabel(item.labelKey, item.label, t),
          hint: groupLabel,
          to: item.route,
          kind: "nav",
        });
      }
    }
    return commands;
  }, [navigation, t]);

  const filteredNav = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return navItems.slice(0, 12);
    return navItems.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        (c.hint?.toLowerCase().includes(q) ?? false),
    );
  }, [navItems, query]);

  const entityItems: CommandItem[] = useMemo(
    () =>
      entityResults.map((r) => ({
        id: `${r.type}-${r.id}`,
        label: r.title,
        hint: `${r.type} · ${r.module}`,
        to: r.path,
        kind: "entity" as const,
      })),
    [entityResults],
  );

  const items = useMemo(
    () => [...entityItems, ...filteredNav].slice(0, 30),
    [entityItems, filteredNav],
  );

  useEffect(() => {
    if (!open) {
      setQuery("");
      setEntityResults([]);
      setActiveIndex(0);
      return;
    }
    const q = debouncedQuery.trim();
    if (q.length < 2) {
      setEntityResults([]);
      return;
    }
    setSearching(true);
    searchApi
      .global(q)
      .then(setEntityResults)
      .catch(() => setEntityResults([]))
      .finally(() => setSearching(false));
  }, [open, debouncedQuery]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, entityResults.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(0, items.length - 1)));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      }
      if (e.key === "Enter" && items[activeIndex]) {
        e.preventDefault();
        navigate(items[activeIndex].to);
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, items, activeIndex, navigate, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 p-4 pt-[12vh]">
      <div
        className="w-full max-w-xl animate-fade-in overflow-hidden rounded-xl border border-slate-200 bg-white shadow-panel dark:border-slate-700 dark:bg-slate-900"
        role="dialog"
        aria-label={t("commandPalette.ariaLabel")}
      >
        <div className="flex items-center gap-2 border-b border-slate-200 px-3 dark:border-slate-700">
          <SearchIcon className="text-slate-400" fontSize="small" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("commandPalette.placeholder")}
            className="flex-1 border-0 bg-transparent py-3 text-sm outline-none dark:text-slate-100"
          />
          <kbd className="hidden rounded border border-slate-200 px-1.5 py-0.5 text-[10px] text-slate-400 sm:inline dark:border-slate-600">
            ESC
          </kbd>
          <button
            type="button"
            onClick={onClose}
            className="workspace-header-icon-btn shrink-0"
            aria-label={t("commandPalette.close")}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <ul className="max-h-80 overflow-y-auto py-1" aria-busy={searching}>
          {items.length === 0 ? (
            <li className="px-4 py-6 text-center text-sm text-slate-500">
              {searching ? t("commandPalette.searching") : t("commandPalette.noMatches")}
            </li>
          ) : (
            items.map((item, index) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={cn(
                    "flex w-full items-center justify-between px-4 py-2.5 text-left text-sm",
                    index === activeIndex
                      ? "bg-primary-50 text-primary-800 dark:bg-primary-950 dark:text-primary-200"
                      : "text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800",
                  )}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => {
                    navigate(item.to);
                    onClose();
                  }}
                >
                  <span>{item.label}</span>
                  <span className="text-xs text-slate-400">{item.hint}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
